import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import { FaSave, FaSync } from "react-icons/fa";
import { toast } from "react-toastify";
import { get, put, post } from "../../utils/makeRequest";
import {
  buildBidAskFromMids,
  deriveMidFromBidAsk,
  formatPremium,
  isQuotedMidValue,
} from "../../utils/forwardSpreadCalc";

const emptyTenors = () =>
  Array.from({ length: 12 }, (_, i) => ({
    tenor_month: i + 1,
    mid_premium: "",
    bid_premium: "",
    ask_premium: "",
    settlement_date: "",
  }));

const emptyCalendar = () => ({
  today_date: "",
  trade_date: "",
  spot_settlement_date: "",
  forward_start_date: "",
  premium_divisor: 100,
});

// Pairs that support the CCIL month-end curve workflow (auto month-end dates
// + interpolation to the 1M–12M curve). Currently USD/INR only.
const MONTH_END_PAIRS = ["USDINR"];
const supportsMonthEnd = (pair) =>
  MONTH_END_PAIRS.includes(String(pair || "").toUpperCase());

const canonicalPremiumDivisor = (pair) => {
  const p = String(pair || "")
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();
  if (p === "USDJPY" || p.endsWith("INR")) return 100;
  return 10000;
};

const resolvePremiumDivisor = (pair, raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return canonicalPremiumDivisor(pair);
  return n;
};

const normalizeStoredMid = (pair, mid, bid, ask) => {
  if (mid === "" || mid == null) {
    const b = Number(bid);
    const a = Number(ask);
    if (
      Number.isFinite(b) &&
      Number.isFinite(a) &&
      !(b === 0 && a === 0)
    ) {
      return deriveMidFromBidAsk(bid, ask);
    }
    return "";
  }
  const n = Number(mid);
  if (!Number.isFinite(n)) return "";
  const b = Number(bid);
  const a = Number(ask);
  const hasBidAsk =
    Number.isFinite(b) &&
    Number.isFinite(a) &&
    !(b === 0 && a === 0);
  if (n === 0 && !hasBidAsk) return "";
  return formatPremium(pair, mid);
};

const ForwardPremiumsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [warming, setWarming] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [sourcePairs, setSourcePairs] = useState([]);
  const [derivedPairs, setDerivedPairs] = useState([]);
  const [selectedPair, setSelectedPair] = useState("USDINR");
  const [tenors, setTenors] = useState(emptyTenors());
  const [calendar, setCalendar] = useState(emptyCalendar());
  const [premiumDivisor, setPremiumDivisor] = useState(100);
  const [readOnly, setReadOnly] = useState(false);
  const [derivedMeta, setDerivedMeta] = useState(null);
  const [error, setError] = useState(null);
  const spreadConfigRef = useRef({});
  const [pairLoading, setPairLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("premiums");
  const [monthEndRows, setMonthEndRows] = useState([]);
  const [monthEndLoading, setMonthEndLoading] = useState(false);
  const [monthEndSaving, setMonthEndSaving] = useState(false);

  const selectedMeta = useMemo(
    () => pairs.find((p) => p.pair === selectedPair),
    [pairs, selectedPair]
  );

  const recalcBidAskFromMids = useCallback(
    (rows, pair = selectedPair, divisor = premiumDivisor) => {
      const hasAnyMid = rows.some((row) => row.mid_premium !== "" && row.mid_premium != null);
      if (!hasAnyMid) return rows;
      const recalced = buildBidAskFromMids(
        pair,
        rows,
        spreadConfigRef.current,
        Number(divisor) || canonicalPremiumDivisor(pair)
      );
      return recalced.map((row) => {
        const original = rows.find((r) => r.tenor_month === row.tenor_month);
        return {
          ...row,
          mid_premium: original?.mid_premium ?? row.mid_premium,
        };
      });
    },
    [selectedPair, premiumDivisor]
  );

  const loadSpreadConfig = async () => {
    try {
      const res = await get("/admin/forward-premiums/spread-config");
      const spreads = res?.spreads || {};
      spreadConfigRef.current = spreads;
      return spreads;
    } catch {
      spreadConfigRef.current = {};
      return {};
    }
  };

  const loadList = async () => {
    const res = await get("/admin/forward-premiums");
    setPairs(res?.pairs || []);
    setSourcePairs(res?.source_pairs || []);
    setDerivedPairs(res?.derived_inr_pairs || []);
    return res?.pairs || [];
  };

  const loadPair = useCallback(async (pair, spreadOverride) => {
    const spreads = spreadOverride ?? spreadConfigRef.current;
    const res = await get(`/admin/forward-premiums/${pair}`);
    const divisor = resolvePremiumDivisor(
      pair,
      res?.premium_divisor ?? res?.pair_config?.premium_divisor
    );
    const rows = res?.tenors?.length
      ? res.tenors.map((t) => {
          const bid = t.bid_premium ?? "";
          const ask = t.ask_premium ?? "";
          const mid =
            t.mid_premium ??
            t.mid_points ??
            deriveMidFromBidAsk(bid, ask);
          return {
            tenor_month: t.tenor_month,
            mid_premium: normalizeStoredMid(pair, mid, bid, ask),
            bid_premium: bid,
            ask_premium: ask,
            settlement_date: t.settlement_date || "",
          };
        })
      : emptyTenors();
    const map = new Map(rows.map((r) => [r.tenor_month, r]));
    const fullRows = Array.from({ length: 12 }, (_, i) => {
      const tenor = i + 1;
      return (
        map.get(tenor) || {
          tenor_month: tenor,
          mid_premium: "",
          bid_premium: "",
          ask_premium: "",
          settlement_date: "",
        }
      );
    });
    const hasAnyMid = fullRows.some(
      (row) => row.mid_premium !== "" && row.mid_premium != null
    );
    setTenors(
      hasAnyMid
        ? buildBidAskFromMids(pair, fullRows, spreads, divisor)
        : fullRows
    );
    setCalendar({
      today_date: res?.calendar?.today_date || "",
      trade_date: res?.calendar?.trade_date || "",
      spot_settlement_date: res?.calendar?.spot_settlement_date || "",
      forward_start_date: res?.calendar?.forward_start_date || "",
      premium_divisor: divisor,
    });
    setPremiumDivisor(divisor);
    setReadOnly(Boolean(res?.read_only || res?.derived));
    setDerivedMeta(res?.pair_config || null);
  }, []);

  const loadMonthEnd = useCallback(async (pair) => {
    if (!supportsMonthEnd(pair)) {
      setMonthEndRows([]);
      return;
    }
    try {
      setMonthEndLoading(true);
      const res = await get(`/admin/forward-premiums/${pair}/month-end`);
      const rows = (res?.rows || []).map((r) => ({
        point_date: r.point_date,
        mid_points:
          r.mid_points === null || r.mid_points === undefined
            ? ""
            : String(r.mid_points),
      }));
      setMonthEndRows(rows);
    } catch (err) {
      console.error("loadMonthEnd:", err);
      setMonthEndRows([]);
    } finally {
      setMonthEndLoading(false);
    }
  }, []);

  useEffect(() => {
    setTenors((prev) => recalcBidAskFromMids(prev));
  }, [premiumDivisor, recalcBidAskFromMids]);

  useEffect(() => {
    if (supportsMonthEnd(selectedPair)) {
      loadMonthEnd(selectedPair);
    } else {
      setMonthEndRows([]);
      setActiveTab("premiums");
    }
  }, [selectedPair, loadMonthEnd]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const spreads = await loadSpreadConfig();
        const list = await loadList();
        const initial =
          list.find((p) => p.pair === "USDINR")?.pair ||
          list[0]?.pair ||
          "USDINR";
        if (cancelled) return;
        setSelectedPair(initial);
        await loadPair(initial, spreads);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError("Failed to load forward premiums");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePairChange = async (e) => {
    const pair = e.target.value;
    setSelectedPair(pair);
    try {
      setPairLoading(true);
      await loadPair(pair);
    } catch (err) {
      toast.error("Failed to load pair premiums");
    } finally {
      setPairLoading(false);
    }
  };

  const updateMidTenor = (tenor, value) => {
    if (readOnly) return;
    setTenors((prev) => {
      const next = prev.map((row) =>
        row.tenor_month === tenor
          ? {
              ...row,
              mid_premium: value,
              bid_premium: isQuotedMidValue(value) ? row.bid_premium : "",
              ask_premium: isQuotedMidValue(value) ? row.ask_premium : "",
            }
          : row
      );
      return isQuotedMidValue(value) ? recalcBidAskFromMids(next) : next;
    });
  };

  const buildSavePayload = () => ({
    premium_divisor:
      Number(premiumDivisor) || canonicalPremiumDivisor(selectedPair),
    tenors: tenors
      .filter((t) =>
        readOnly
          ? Number.isFinite(Number(t.bid_premium)) &&
            Number.isFinite(Number(t.ask_premium))
          : isQuotedMidValue(t.mid_premium)
      )
      .map((t) => ({
        tenor_month: t.tenor_month,
        bid_premium: Number(t.bid_premium),
        ask_premium: Number(t.ask_premium),
      })),
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = buildSavePayload();
      if (!payload.tenors.length) {
        toast.warn(
          readOnly
            ? "No bid/ask premiums to save — load pair data first"
            : "Enter at least one MID premium before saving"
        );
        setSaving(false);
        return;
      }
      await put(`/admin/forward-premiums/${selectedPair}`, payload);
      toast.success(
        readOnly
          ? "Premium divisor saved to database and Redis"
          : "Saved to database and Redis"
      );
      await loadList();
      await loadPair(selectedPair);
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.error || "Failed to save forward premiums";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleWarm = async () => {
    try {
      setWarming(true);
      await post("/admin/forward-premiums/warm", {});
      toast.success("Redis re-warmed from database");
    } catch (err) {
      toast.error("Redis warm failed");
    } finally {
      setWarming(false);
    }
  };

  const handleDeriveCrosses = async () => {
    try {
      setCalculating(true);
      const res = await post("/admin/forward-premiums/derive-crosses", {});
      toast.success(
        `INR crosses updated (${(res.saved_pairs || []).length} pairs)`
      );
      await loadList();
      await loadPair(selectedPair);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to derive INR crosses");
    } finally {
      setCalculating(false);
    }
  };

  const updateMonthEndRate = (pointDate, value) => {
    setMonthEndRows((prev) =>
      prev.map((row) =>
        row.point_date === pointDate ? { ...row, mid_points: value } : row
      )
    );
  };

  const collectMonthEndPoints = () =>
    monthEndRows
      .filter((row) => {
        const n = Number(row.mid_points);
        return row.mid_points !== "" && Number.isFinite(n);
      })
      .map((row) => ({
        point_date: row.point_date,
        mid_points: Number(row.mid_points),
      }));

  const applyPreviewToTenors = (preview) => {
    const byTenor = new Map(
      (preview || []).map((row) => [Number(row.tenor_month), row])
    );
    setTenors(
      Array.from({ length: 12 }, (_, i) => {
        const tenor = i + 1;
        const row = byTenor.get(tenor);
        if (!row || row.mid_points == null) {
          return {
            tenor_month: tenor,
            mid_premium: "",
            bid_premium: "",
            ask_premium: "",
            settlement_date: row?.settlement_date || "",
          };
        }
        return {
          tenor_month: tenor,
          mid_premium: formatPremium(selectedPair, row.mid_points),
          bid_premium: row.bid_premium ?? "",
          ask_premium: row.ask_premium ?? "",
          settlement_date: row.settlement_date || "",
          interpolated: Boolean(row.interpolated),
        };
      })
    );
  };

  const handleMonthEndCalculate = async ({ save }) => {
    const points = collectMonthEndPoints();
    if (!points.length) {
      toast.warn("Enter at least one month-end rate first");
      return;
    }
    try {
      setMonthEndSaving(true);
      setError(null);
      const body = {
        points,
        premium_divisor:
          Number(premiumDivisor) || canonicalPremiumDivisor(selectedPair),
      };
      const endpoint = save
        ? `/admin/forward-premiums/${selectedPair}/calculate-and-save`
        : `/admin/forward-premiums/${selectedPair}/calculate`;
      const res = await post(endpoint, body);
      const preview = res?.preview || res?.tenors || [];
      applyPreviewToTenors(preview);
      setActiveTab("premiums");
      if (save) {
        toast.success("Month-end curve calculated and saved to DB + Redis");
        await loadList();
        await loadPair(selectedPair);
        await loadMonthEnd(selectedPair);
      } else {
        toast.success("1M–12M MID premiums calculated from month-end rates");
      }
    } catch (err) {
      console.error("handleMonthEndCalculate:", err);
      const msg =
        err.response?.data?.error || "Failed to calculate month-end curve";
      setError(msg);
      toast.error(msg);
    } finally {
      setMonthEndSaving(false);
    }
  };

  const renderPairOptions = () => {
    const source = pairs.filter((p) => sourcePairs.includes(p.pair));
    const derived = pairs.filter((p) => derivedPairs.includes(p.pair));
    const other = pairs.filter(
      (p) => !sourcePairs.includes(p.pair) && !derivedPairs.includes(p.pair)
    );

    return (
      <>
        {source.length > 0 && (
          <optgroup label="Source pairs (9)">
            {source.map((p) => (
              <option key={p.pair} value={p.pair}>
                {p.display_pair}
                {p.configured ? "" : " (empty)"}
              </option>
            ))}
          </optgroup>
        )}
        {derived.length > 0 && (
          <optgroup label="INR crosses (auto-derived)">
            {derived.map((p) => (
              <option key={p.pair} value={p.pair}>
                {p.display_pair}
                {p.configured ? " ✓" : " (empty)"}
              </option>
            ))}
          </optgroup>
        )}
        {other.length > 0 && (
          <optgroup label="Other pairs">
            {other.map((p) => (
              <option key={p.pair} value={p.pair}>
                {p.display_pair}
                {p.configured ? "" : " (empty)"}
              </option>
            ))}
          </optgroup>
        )}
      </>
    );
  };

  if (loading && !pairs.length) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <h3 className="mb-1">Forward Premiums</h3>
          <p className="text-muted mb-0">
            Enter 1M–12M MID premiums per settlement date — Bid and Ask
            auto-calculate from the spread engine. INR crosses are auto-derived
            from 9 source pairs.
          </p>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3} lg={2}>
              <Form.Group>
                <Form.Label>Currency pair</Form.Label>
                <Form.Select value={selectedPair} onChange={handlePairChange}>
                  {renderPairOptions()}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} lg={2}>
              <Form.Group>
                <Form.Label>Today date</Form.Label>
                <Form.Control
                  type="text"
                  value={calendar.today_date || "—"}
                  readOnly
                  className="bg-light"
                />
              </Form.Group>
            </Col>
            <Col md={3} lg={2}>
              <Form.Group>
                <Form.Label>Spot settlement date</Form.Label>
                <Form.Control
                  type="text"
                  value={calendar.spot_settlement_date || "—"}
                  readOnly
                  className="bg-light"
                />
              </Form.Group>
            </Col>
            <Col md={3} lg={2}>
              <Form.Group>
                <Form.Label>Forward start date</Form.Label>
                <Form.Control
                  type="text"
                  value={calendar.forward_start_date || "—"}
                  readOnly
                  className="bg-light"
                />
              </Form.Group>
            </Col>
            <Col md={3} lg={2}>
              <Form.Group>
                <Form.Label>Premium divisor</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  min="1"
                  value={premiumDivisor}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setPremiumDivisor("");
                      return;
                    }
                    const n = Number(raw);
                    if (Number.isFinite(n) && n > 0) setPremiumDivisor(n);
                  }}
                  disabled={false}
                  title={
                    readOnly
                      ? "Editable for auto-derived pairs — updates bid/ask spread display; MID premiums stay from source derivation"
                      : canonicalPremiumDivisor(selectedPair) === 100
                        ? "USD/INR & USD/JPY: divisor 100; INR crosses: paise (100)"
                        : "FX pairs: display points (default divisor 10000)"
                  }
                />
              </Form.Group>
            </Col>
            <Col md={12} lg={2} className="d-flex gap-2 justify-content-lg-end flex-wrap">
              <Button
                variant="outline-secondary"
                onClick={handleWarm}
                disabled={warming}
                size="sm"
              >
                {warming ? <Spinner size="sm" className="me-1" /> : <FaSync className="me-1" />}
                Warm Redis
              </Button>
              <Button
                variant="outline-info"
                onClick={handleDeriveCrosses}
                disabled={calculating}
                size="sm"
              >
                Derive INR Crosses
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving}
                size="sm"
                title={
                  readOnly
                    ? "Save premium divisor (auto-derived pair)"
                    : "Save premiums"
                }
              >
                {saving ? <Spinner size="sm" className="me-1" /> : <FaSave className="me-1" />}
                Save
              </Button>
            </Col>
          </Row>
          {selectedMeta && (
            <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
              <small className="text-muted">
                Status:{" "}
                {selectedMeta.configured
                  ? "Premiums configured"
                  : "No premiums yet — enter values and save"}
                {pairLoading ? " · loading pair…" : ""}
              </small>
              {readOnly && (
                <Badge bg="secondary">Auto-derived from source pairs</Badge>
              )}
              {derivedMeta?.derived_at && (
                <small className="text-muted">
                  Derived at: {new Date(derivedMeta.derived_at).toLocaleString()}
                </small>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {(() => {
      const premiumsTable = (
      <>
      <Card className="shadow-sm mb-3">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "80px" }}>Tenor</th>
                <th style={{ width: "160px" }}>Settlement date</th>
                <th>MID premium</th>
                <th>Bid premium</th>
                <th>Ask premium</th>
              </tr>
            </thead>
            <tbody>
              {tenors.map((row) => (
                <tr key={row.tenor_month}>
                  <td className="fw-semibold">{row.tenor_month}M</td>
                  <td>
                    <Form.Control
                      type="text"
                      value={row.settlement_date || "—"}
                      readOnly
                      className="bg-light"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      step="0.0001"
                      value={row.mid_premium}
                      onChange={(e) =>
                        updateMidTenor(row.tenor_month, e.target.value)
                      }
                      placeholder="Enter MID"
                      readOnly={readOnly}
                      className={`${readOnly ? "bg-light" : ""} ${row.interpolated ? "border-info" : ""}`}
                      title={
                        row.interpolated
                          ? "Auto-interpolated from nearest known tenors (empty/0 treated as missing)"
                          : "Enter MID — Bid/Ask auto-calculate"
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={row.bid_premium}
                      readOnly
                      placeholder="Auto"
                      className="bg-light"
                      title="Auto-calculated from MID using spread engine"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={row.ask_premium}
                      readOnly
                      placeholder="Auto"
                      className="bg-light"
                      title="Auto-calculated from MID using spread engine"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <p className="text-muted small mt-3 mb-0">
        Enter MID premium per tenor — Bid and Ask auto-calculate using the
        spread engine (BaseHalfSpread × TenorMultiplier). Any missing or 0
        tenors auto-interpolate from the nearest known anchors (e.g. 1M+12M
        fills 2M–11M; 6M+9M fills 7M/8M).
        Save writes Bid/Ask to DB + Redis. INR crosses derive automatically.
      </p>
      </>
      );

      const monthEndTable = (
      <>
      <Card className="shadow-sm mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h6 className="mb-1">CCIL Month-End Curve → 1M–12M MID</h6>
              <small className="text-muted">
                Month-end dates auto-derive from the forward start date using the
                Modified Following convention (USD + INR holidays). Enter each
                CCIL month-end premium in paise — the 1M–12M MID auto-computes by
                linear interpolation, and Bid/Ask follow the spread engine.
              </small>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleMonthEndCalculate({ save: false })}
                disabled={monthEndSaving || monthEndLoading}
              >
                {monthEndSaving ? (
                  <Spinner size="sm" className="me-1" />
                ) : null}
                Calculate 1M–12M
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleMonthEndCalculate({ save: true })}
                disabled={monthEndSaving || monthEndLoading}
              >
                {monthEndSaving ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <FaSave className="me-1" />
                )}
                Save &amp; Calculate
              </Button>
            </div>
          </div>
          {monthEndLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading month-end dates…
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "80px" }}>#</th>
                  <th style={{ width: "200px" }}>Month-end date</th>
                  <th>Premium (paise)</th>
                </tr>
              </thead>
              <tbody>
                {monthEndRows.map((row, idx) => (
                  <tr key={row.point_date}>
                    <td className="fw-semibold">
                      {idx === monthEndRows.length - 1 ? (
                        <Badge bg="light" text="dark" title="Trailing month-end used only as interpolation headroom for 12M">
                          +1
                        </Badge>
                      ) : (
                        idx + 1
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={row.point_date}
                        readOnly
                        className="bg-light"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.0001"
                        value={row.mid_points}
                        onChange={(e) =>
                          updateMonthEndRate(row.point_date, e.target.value)
                        }
                        placeholder="Enter CCIL premium in paise"
                      />
                    </td>
                  </tr>
                ))}
                {!monthEndRows.length && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-4">
                      No month-end dates available.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <p className="text-muted small mt-3 mb-0">
        Premium(T) = P_prev + [(T − D_prev) / (D_next − D_prev)] × (P_next −
        P_prev). An anchor of 0 at the forward start date is added automatically,
        and one trailing month-end past 12M gives the final tenor room to
        interpolate. Use “Save &amp; Calculate” to persist the curve to DB +
        Redis.
      </p>
      </>
      );

      if (!supportsMonthEnd(selectedPair)) return premiumsTable;

      return (
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "premiums")}
          className="mb-3"
        >
          <Tab eventKey="premiums" title="1M–12M Premiums">
            {premiumsTable}
          </Tab>
          <Tab eventKey="monthend" title="Month-End Curve (CCIL)">
            {monthEndTable}
          </Tab>
        </Tabs>
      );
      })()}
    </Container>
  );
};

export default ForwardPremiumsTab;
